import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import WelcomeWizard from './pages/Welcome/WelcomeWizard';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { NotificationCenterProvider } from './contexts/NotificationCenterContext';
import { ThemeCustomProvider } from './contexts/ThemeCustomContext';
import ThemeApplier from './components/ThemeApplier';
import { authService } from './services/authService';
import { Box, CircularProgress } from '@mui/material';
import { useState, useEffect } from 'react';
import DashboardPage from './pages/DashboardPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ProfessionalsPage from './pages/ProfessionalsPage';
import PlacesPage from './pages/PlacesPage';
import SchedulesPage from './pages/SchedulesPage';
import ScheduleBlocksPage from './pages/ScheduleBlocksPage';
import PersonsPage from './pages/PersonsPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PagesPage from './pages/PagesPage';
import PageEditorPage from './pages/PageEditorPage';
import ServicesPage from './pages/ServicesPage';
import { SupportTicketsPage } from './components/SupportTickets';
import ReportsPage from './pages/ReportsPage';
import AccessPage from './pages/AccessPage';
import FinancePage from './pages/FinancePage';
import PlansPage from './pages/PlansPage';
import UpgradeProvider from './components/plans/UpgradeProvider';
import ApiHealthPage from './pages/ApiHealthCheck/ApiHealthPage';
import { isDevEnvironment } from './utils/environment';

import LgpdConsentModal from './components/LgpdConsentModal';
import QueryInvalidator from './components/QueryInvalidator';
// Tema customizado com otimizações mobile
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    
    // Títulos principais - responsivos e compactos para admin
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',   // 24px desktop (era 34px - muito grande para admin)
      '@media (max-width:600px)': {
        fontSize: '1.25rem', // 20px mobile
      },
    },
    
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',  // 20px desktop (era 24px)
      '@media (max-width:600px)': {
        fontSize: '1.125rem', // 18px mobile
      },
    },
    
    h6: {
      fontWeight: 600,
      fontSize: '1.0625rem', // 17px desktop (era 20px)
      '@media (max-width:600px)': {
        fontSize: '0.9375rem', // 15px mobile
      },
    },
    
    // Corpo de texto
    body1: {
      fontSize: '1rem',  // 16px desktop
      '@media (max-width:600px)': {
        fontSize: '0.9375rem',  // 15px mobile (-6%)
      },
    },
    
    body2: {
      fontSize: '0.875rem',  // 14px desktop
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',  // 13px mobile (-7%)
      },
    },
    
    // Botões
    button: {
      textTransform: 'none',
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
      },
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '@media (max-width:600px)': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '@media (max-width:600px)': {
            padding: 12,  // -25% padding mobile
            '&:last-child': {
              paddingBottom: 12,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '@media (max-width:600px)': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (max-width:600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            fontSize: '0.875rem',
          },
        },
      },
    },
    // ──────────────────────────────────────────
    // Formulários: padrão compacto em toda a app
    // ──────────────────────────────────────────
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
    },
  },
});

// Configuração do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos - dados considerados "frescos"
      cacheTime: 10 * 60 * 1000, // 10 minutos - tempo em cache
      refetchOnWindowFocus: false, // Não refetch ao focar janela
      retry: 1, // Tentar apenas 1 vez em caso de erro
    },
  },
});

function App() {
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(true);

  // Detectar isNewUser do JWT
  useEffect(() => {
    try {
      // SUPORTE A TESTE: Verificar se há ?newuser=true na URL
      const queryParams = new URLSearchParams(window.location.search);
      const forceNewUser = queryParams.get('newuser') === 'true';
      
      if (forceNewUser) {
        // Limpar APENAS chaves de welcome, preservar token JWT para as APIs funcionarem
        localStorage.removeItem('agendario_welcome_session');
        localStorage.removeItem('agendario_onboarding_session');
        const userId = authService.getUserInfo()?.userId;
        localStorage.removeItem(userId ? `agendario_theme_${userId}` : 'agendario_theme');
        localStorage.removeItem('agendario_wizard_state');
        sessionStorage.clear();
        setIsNewUser(true);
        setLoading(false);
        return;
      }
      
      // PRIMEIRO: Processar token da URL (se houver)
      const hasNewToken = authService.processLoginCallback();
      
      // DEPOIS: Tentar obter userInfo
      const userInfo = authService.getUserInfo();
      
      if (userInfo) {
        const newUser = userInfo.isNewUser === true;
        setIsNewUser(newUser);
      } else {
        // Nenhuma informação de usuário no JWT - renderizará admin normal
      }
      setLoading(false);
    } catch (error) {
      console.warn('[APP] Erro ao detectar isNewUser:', error);
      setLoading(false);
    }
  }, []);

  // Log quando isNewUser ou loading mudam
  useEffect(() => {
  }, [isNewUser, loading]);

  // Mostrar loading enquanto detecta isNewUser
  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#ffffff' }} />
      </Box>
    );
  }

  // Se for novo usuário, verificar se já completou welcome
  let welcomeCompleted = false;
  try {
    const welcomeSessionData = localStorage.getItem('agendario_welcome_session');
    if (welcomeSessionData) {
      const parsed = JSON.parse(welcomeSessionData);
      welcomeCompleted = !!parsed.completedAt;
    }
  } catch (error) {
    // localStorage corrompido — tratar como welcome não concluído
    console.warn('[App.jsx] localStorage "agendario_welcome_session" inválido:', error.message);
    localStorage.removeItem('agendario_welcome_session');
    welcomeCompleted = false;
  }
  
  // Se for novo usuário E NÃO completou welcome, mostrar wizard
  if (isNewUser && !welcomeCompleted) {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeCustomProvider disableThemeSelection={true}>
          <ThemeApplier baseTheme={theme}>
            <CssBaseline />
            <BrowserRouter>
              <WelcomeWizard />
            </BrowserRouter>
          </ThemeApplier>
        </ThemeCustomProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    );
  }

  // Admin normal
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeCustomProvider>
        <ThemeApplier baseTheme={theme}>
          <CssBaseline />
          <OnboardingProvider>
            <NotificationCenterProvider>
              <BrowserRouter>
              <UpgradeProvider>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<IndexRedirect />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="appointments" element={<AppointmentsPage />} />
                  <Route path="finance" element={<FinancePage />} />
                  <Route path="plans" element={<PlansPage />} />
                  <Route path="professionals" element={<ProfessionalsPage />} />
                  <Route path="places" element={<PlacesPage />} />
                  <Route path="services" element={<ServicesPage />} />
                  <Route path="pages" element={<PagesPage />} />
                  <Route path="pages/new/:pageType" element={<PageEditorPage />} />
                  <Route path="pages/edit/:pageId" element={<PageEditorPage />} />
                  <Route path="schedules" element={<SchedulesPage />} />
                  <Route path="persons" element={<PersonsPage />} />
                  <Route path="absences" element={<ScheduleBlocksPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="access" element={<AccessPage />} />
                  <Route path="support-tickets" element={<SupportTicketsPage />} />
                  {isDevEnvironment() && (
                    <Route path="api-health" element={<ApiHealthPage />} />
                  )}
                </Route>
                <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
              </Routes>
              <LgpdConsentModal />
              </UpgradeProvider>
            </BrowserRouter>
            </NotificationCenterProvider>
          </OnboardingProvider>
        </ThemeApplier>
      </ThemeCustomProvider>
      <QueryInvalidator />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

// Redireciona / para /dashboard preservando hash e search.
// Necessário para deep links do tipo /#notification={id} chegarem ao Layout
// sem que o Navigate descarte o hash durante o render.
function IndexRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/dashboard', search: location.search, hash: location.hash }} replace />;
}
export default App;
